import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useMemo,
	useRef
} from "react";
import { SpaceOptions, SpaceRenderState } from "./RenderState/common";
import {
	SpaceKind,
	UserWorkspacePermissionsModel
} from "client/Api/Workspace/workspace";
import { localize } from "client_legacy/ndaw/culture";
import {
	TileGroup,
	TileGroupCallbacks
} from "./TileGroup";
import classNames from "classnames";
import { TimeZone } from "client/DateAndTime/timezones";
import { TileContentDependencies } from "./useTileContentDependencies";
import { DragCallbackArgs } from "client/Hooks";
import { DatabaseOrTempObjectId } from "client_legacy/ndaw/tempOrDatabaseId";
import { GridPosition } from "client/Api/Workspace/common";
import { Coordinate } from "client/Misc/commonTypes";
import { TileCallbacks } from "./Tile";
import { mergeRefs } from "client/Misc/reactUtils";
import {
	applyTileDragging,
	applyTileGroupDragging,
	commitTileDrag,
	commitTileGroupDrag
} from "./RenderState/reducers/space";
import { setTrackingId } from "client/Misc/utils";
import styles from "./TileGroupGrid.module.less";
import {
	convertDimensionToPx,
	GridCellPixelValues
} from "./GridCellPixelValuesTracker";
import { TileGroupPlaceholder } from "./TileGroupPlaceholder";

export type TileGroupGridProps = {
	renderState: SpaceRenderState;
	cellPixelValues: GridCellPixelValues;
	tileGridCellPixelValues: GridCellPixelValues;
	permissions: UserWorkspacePermissionsModel;
	userTimezone: TimeZone;
	tileContentDependencies: TileContentDependencies;
	onMutateRenderState: (mutation: (state: SpaceRenderState) => SpaceRenderState) => void;
	tileGroupCallbacks: Omit<TileGroupCallbacks, "onDragStart" | "onDrag" | "onDragStop">;
	tileCallbacks: Omit<TileCallbacks, "onDragStart" | "onDragMove" | "onDragStop">;
	isOnImage: boolean;
};

export const TileGroupGrid = forwardRef(
	(props: TileGroupGridProps, ref: Ref<HTMLDivElement>): ReactElement => {
		const {
			renderState,
			cellPixelValues,
			tileGridCellPixelValues,
			permissions,
			userTimezone,
			tileContentDependencies,
			onMutateRenderState,
			tileGroupCallbacks,
			tileCallbacks,
			isOnImage
		} = props;

		const internalRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

		const isVisualEmpty = useMemo(
			(): boolean => {
				return !renderState.groups
					.some((x) => x.isVisible);
			},
			[renderState.groups]
		);

		const {
			options: spaceOptions,
			placeholder,
			groups,
			isInEditMode
		} = renderState;

		const canRearrangeTileGroups: boolean = spaceOptions.isSet
			&& spaceOptions.value.canRearrangeTileGroupsInSpace;

		const tileGroupDragMoveHandler = useCallback(
			(tileGroupId: DatabaseOrTempObjectId, args: DragCallbackArgs) => {
				const { current: container } = internalRef;
				if (!container) {
					return;
				}

				const position: GridPosition = getTileGroupDragPositionFromDragEvent(
					args,
					container,
					cellPixelValues
				);

				onMutateRenderState(
					(x): SpaceRenderState => applyTileGroupDragging(
						x,
						tileGroupId,
						position
					)
				);
			},
			[cellPixelValues, onMutateRenderState]
		);

		const tileGroupDragStopHandler = useCallback(
			(tileGroupId: DatabaseOrTempObjectId, args: DragCallbackArgs) => {
				const { current: container } = internalRef;
				if (!container) {
					return;
				}

				const position: GridPosition = getTileGroupDragPositionFromDragEvent(
					args,
					container,
					cellPixelValues
				);

				onMutateRenderState(
					(x): SpaceRenderState => commitTileGroupDrag(
						x,
						tileGroupId,
						position
					)
				);
			},
			[cellPixelValues, onMutateRenderState]
		);

		const tileDragMoveHandler = useCallback(
			(tileId: number, args: DragCallbackArgs) => {
				const { current: container } = internalRef;
				if (!container) {
					return;
				}

				const position: GridPosition = getTileDragPositionFromDragEvent(
					args,
					container,
					tileGridCellPixelValues
				);

				onMutateRenderState(
					(x): SpaceRenderState => applyTileDragging(
						x,
						tileId,
						position
					)
				);
			},
			[onMutateRenderState, tileGridCellPixelValues]
		);

		const tileDragStopHandler = useCallback(
			(tileId: number, args: DragCallbackArgs) => {
				const { current: container } = internalRef;
				if (!container) {
					return;
				}

				const position: GridPosition = getTileDragPositionFromDragEvent(
					args,
					container,
					tileGridCellPixelValues
				);

				onMutateRenderState(
					(x): SpaceRenderState => commitTileDrag(
						x,
						tileId,
						position
					)
				);
			},
			[onMutateRenderState, tileGridCellPixelValues]
		);

		return (
			<div
				className={styles.root}
				style={convertDimensionToPx(
					renderState.dimension,
					cellPixelValues,
					// Don't trim the last gutter as the design tells it includes it spans the gutter below it
					{ rowGutter: true }
				)}
				ref={mergeRefs(internalRef, ref)}
			>
				{isVisualEmpty && (
					<TileGroupGridEmptyMessage
						options={renderState.options}
						isInEditMode={renderState.isInEditMode}
						isOnImage={isOnImage}
					/>
				)}

				{placeholder && (
					<TileGroupPlaceholder
						placeholder={placeholder}
						cellPixelValues={cellPixelValues}
					/>
				)}

				{groups.map(
					(x): ReactElement | null => {
						if (!x.isVisible) {
							return null;
						}

						const { data: { id } } = x;

						return (
							<TileGroup
								key={id.hasDatabaseId
									? id.databaseId
									: id.tempId
								}
								isInEditMode={isInEditMode}
								renderState={x}
								groupCellPixelValues={cellPixelValues}
								tileCellPixelValues={tileGridCellPixelValues}
								permissions={permissions}
								userTimezone={userTimezone}
								tileContentDependencies={tileContentDependencies}
								tileGroupCallbacks={{
									...tileGroupCallbacks,
									onDragStart: tileGroupDragMoveHandler,
									onDrag: tileGroupDragMoveHandler,
									onDragStop: tileGroupDragStopHandler
								}}
								tileCallbacks={{
									...tileCallbacks,
									onDragStart: tileDragMoveHandler,
									onDragMove: tileDragMoveHandler,
									onDragStop: tileDragStopHandler
								}}
								canRearrangeTileGroups={canRearrangeTileGroups}
								isOnImage={isOnImage}
							/>
						);
					}
				)}
			</div>
		);
	}
);
TileGroupGrid.displayName = "TileGroupGrid";

type TileGroupGridEmptyMessageProps = {
	options: OptionalModel<SpaceOptions>;
	isInEditMode: boolean;
	isOnImage: boolean;
};

const TileGroupGridEmptyMessage = (props: TileGroupGridEmptyMessageProps): ReactElement => {
	const {
		options,
		isInEditMode,
		isOnImage
	} = props;

	const emptyMessageKey = useMemo(
		(): LocalizationKey => {
			if (!options.isSet) {
				return "_CurrentlyYouDontHaveAccessToAnySpacePleaseContactYourAdmin";
			}

			if (options.value.kind === SpaceKind.Personal) {
				return "_EmptyWorkspaceMessage";
			}

			if (isInEditMode) {
				return "_EmptyWorkspaceMessageForSharedSpaceInEditMode";
			}

			if (options.value.canRearrangeTileGroupsInSpace) {
				return "_EmptyWorkspaceMessageForSharedSpace";
			}

			return "_EmptyWorkspaceMessageForNonEditableSharedSpace";
		},
		[options, isInEditMode]
	);

	return (
		<h3
			{...setTrackingId("empty-tile-group-message")}
			className={classNames(
				styles.message,
				{ [styles.message_onImage]: isOnImage }
			)}
		>
			{localize(emptyMessageKey)}
		</h3>
	);
};
TileGroupGridEmptyMessage.displayName = "TileGroupGridEmptyMessage";

function getTileGroupDragPositionFromDragEvent(
	args: DragCallbackArgs,
	container: HTMLDivElement,
	tileGroupCellPixelValues: GridCellPixelValues
): GridPosition {
	const {
		columnWidthWithGutter,
		rowHeightWithGutter,
		columnGutter,
		rowGutter
	} = tileGroupCellPixelValues;

	const { left, top } = container.getBoundingClientRect();

	const positionInPxInGrid: Coordinate = {
		x: args.clientX - left,
		y: args.clientY - top
	};

	// Functionally we care about the grid cells and not the gutters. For the calculations it is easier to work
	// with squares which have no gutter between them. But doing so results in that the middle of the square used
	// in the calculation is not the middle of what we care about functional wise. By adding half of the gutter
	// to the offset we functionally care about fix that.
	// E.g. if the provide offset is in the center of the grid cell it now ends up in the center of the grid cell + gutter
	const adjustedLeftOffset: number = positionInPxInGrid.x + (columnGutter / 2);
	const adjustedTopOffset: number = positionInPxInGrid.y + (rowGutter / 2);
	const position: GridPosition = {
		column: Math.floor((adjustedLeftOffset / columnWidthWithGutter) + 0.5),
		row: Math.floor((adjustedTopOffset / rowHeightWithGutter) + 0.5)
	};

	return position;
}

function getTileDragPositionFromDragEvent(
	args: DragCallbackArgs,
	container: HTMLDivElement,
	tileCellPixelValues: GridCellPixelValues
) : GridPosition {
	const { left, top } = container.getBoundingClientRect();

	const {
		columnWidthWithGutter,
		rowHeightWithGutter,
		columnGutter,
		rowGutter
	} = tileCellPixelValues;

	const positionInGrid: Coordinate = {
		// The "global tile grid" is off by a column gutter from the "tile group grid") (this is the space surrounding the tile grid in the tile group)
		x: args.clientX - (left + columnGutter),
		y: args.clientY - top
	};

	// Functionally we care about the grid cells and not the gutters. For the calculations it is easier to work
	// with squares which have no gutter between them. But doing so results in that the middle of the square used
	// in the calculation is not the middle of what we care about functional wise. By adding half of the gutter
	// to the offset we functionally care about fix that.
	// E.g. if the provide offset is in the center of the grid cell it now ends up in the center of the grid cell + gutter
	const adjustedLeftOffset: number = positionInGrid.x + (columnGutter / 2);
	const adjustedTopOffset: number = positionInGrid.y + (rowGutter / 2);
	const position: GridPosition = {
		column: Math.floor((adjustedLeftOffset / columnWidthWithGutter) + 0.5),
		row: Math.floor((adjustedTopOffset / rowHeightWithGutter) + 0.5)
	};

	return position;
}
